/**
 * useEmployeeExport Hook
 * Custom hook for exporting employees to Excel
 */

import { useState, useCallback } from 'react';
import employeeService from '../services/employeeService';
import { EmployeeFilters } from '../types/employee';
import { useSnackbar } from 'notistack';

interface UseEmployeeExportResult {
  exporting: boolean;
  exportEmployees: (filters?: EmployeeFilters, filename?: string) => Promise<void>;
}

export const useEmployeeExport = (): UseEmployeeExportResult => {
  const { enqueueSnackbar } = useSnackbar();
  
  const [exporting, setExporting] = useState<boolean>(false);

  const exportEmployees = useCallback(async (
    filters?: EmployeeFilters,
    filename = 'funcionarios.xlsx'
  ): Promise<void> => {
    try {
      setExporting(true);
      
      const blob = await employeeService.exportEmployees(filters);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('Funcionários exportados com sucesso', { variant: 'success' });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao exportar funcionários';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setExporting(false);
    }
  }, [enqueueSnackbar]);

  return {
    exporting,
    exportEmployees
  };
};
